import { isNull } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';
import Modal from 'react-modal';

import { Button } from '../../../shared/button/Button';
import { SpaceBetweenButtonWrapper } from '../../../shared/button/SpaceBetweenButtonWrapper';
import { Opinion } from '../../../shared/icons';
import { ModalHeader } from '../../../shared/modal/ModalHeader';
import { Position } from '../../../shared/modal/PositionModal';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { bespokeFormatter, commissionedByFormatter, dateFormatter, scopeFormatter } from '../../../shared/table/arkTableFormatters';
import { Toggle } from '../../../shared/toggle';
import { InformationTooltip } from '../../../shared/tooltip';
import { ArkOpinion } from '../store';
import styles from './UploadOpinions.module.scss';

const { lightestGrey, white } = styles;

interface OpinionConfirmationModalProps {
    isOpen: boolean;
    closeModal: () => void;
    completeOpinion: (completeWithAI: boolean) => void;
    position: Position;
    height: string;
    width: string;
    opinion: ArkOpinion;
}

const opinionKeysToCheck: { key: keyof ArkOpinion; label: string; }[] = [
    { key: 'description', label: 'Description' },
    { key: 'bespoke', label: 'Bespoke or Industry Standard' },
    { key: 'jurisdiction', label: 'Jurisdiction' },
    { key: 'focus', label: 'Focus' },
    { key: 'type', label: 'Type' },
    { key: 'dateOfOpinion', label: 'Date of Opinion' },
    { key: 'scope', label: 'Scope' },
    { key: 'commissionedBy', label: 'Commissioned By' },
    { key: 'counterpartyStartPage', label: 'Counterparty Annex' }
];

export const OpinionConfirmationModal: React.FC<OpinionConfirmationModalProps> = ({ closeModal, position, height, width, isOpen, completeOpinion, opinion }) => {
    const { description, bespoke, jurisdiction, focus, type, dateOfOpinion, scope, commissionedBy, counterpartyStartPage, counterpartyEndPage } = opinion;
    const [allDetailsConfirmed, setAllDetailsConfirmed] = useState<boolean>(false);
    const [completeWithAI, setCompleteWithAI] = useState<boolean>(bespoke === 0);

    const showCompleteWithAIToggle = useMemo(() => bespoke === 0, [bespoke]);

    const getOpinionValue = useCallback((key: keyof ArkOpinion) => {
        switch (key) {
            case 'description':
                return description;
            case 'bespoke':
                return bespokeFormatter(bespoke);
            case 'jurisdiction':
                return jurisdiction!;
            case 'focus':
                return focus!;
            case 'type':
                return type!;
            case 'dateOfOpinion':
                return dateFormatter(dateOfOpinion);
            case 'scope':
                return scopeFormatter(scope);
            case 'commissionedBy':
                return commissionedByFormatter(commissionedBy, opinion);
            case 'counterpartyStartPage': {
                if (isNull(counterpartyStartPage) && isNull(counterpartyEndPage)) {
                    return '';
                }
                return `${counterpartyStartPage || ''} - ${counterpartyEndPage || ''}`;
            }
            default:
                return '';
        }
    }, [opinion, description, bespoke, jurisdiction, focus, type, dateOfOpinion, scope, commissionedBy, counterpartyStartPage, counterpartyEndPage]);

    return (
        <Modal
            isOpen={isOpen}
            className={styles.opinionConfirmationModal}
            ariaHideApp={false}
            style={{
                overlay: {
                    display: 'flex',
                    zIndex: 9,
                    position: 'absolute',
                    left: `${position.x}px`,
                    top: `${position.y}px`,
                    width,
                    height,
                    borderRadius: '5px'
                }
            }}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={false}
            onRequestClose={closeModal}
        >
            <div className={styles.completeConfirmationWrapper}>
                <ModalHeader icon={Opinion} label='Confirm Opinion Details' />
                <div className={styles.completeConfirmationWarning}>
                    To make sure this Opinion is added correctly.<br />
                    Please confirm the details below are correct before completing.
                </div>
                <div className={styles.completeConfirmationDetails}>
                    <Scrollable>
                        {opinionKeysToCheck.map(({ key, label }, index) => (
                            <div key={key} className={styles.opinionDetailWrapper} style={{ backgroundColor: index % 2 ? lightestGrey : white }}>
                                <div className={styles.opinionDetailLabel}>{label}:</div>
                                <div className={styles.opinionDetailValue}>{getOpinionValue(key)}</div>
                            </div>
                        ))}
                    </Scrollable>
                </div>

                <div className={styles.completeWithAIWrapper}>
                    {showCompleteWithAIToggle &&
                        <>
                            <div className={styles.completeWithAILabel}>Complete with AI?</div>
                            <InformationTooltip content='If selected, we will attempt to extract key information from this Opinion using Artificial Intelligence.' />
                            <div className={styles.confirmationToggle}>
                                <Toggle checked={completeWithAI} onChange={setCompleteWithAI} />
                            </div>
                        </>
                    }
                </div>
                <div className={styles.confirmationDisclaimerWrapper}>
                    <div className={styles.confirmationDisclaimer}>I confirm these details are correct</div>
                    <div className={styles.confirmationToggle}>
                        <Toggle checked={allDetailsConfirmed} onChange={setAllDetailsConfirmed} />
                    </div>
                </div>
                <SpaceBetweenButtonWrapper leftButtons={[<Button onClick={closeModal} label='Cancel' key='cancel' />]} rightButtons={[<Button onClick={() => completeOpinion(completeWithAI)} label='Complete' key='complete' disabled={!allDetailsConfirmed} />]} />
            </div>
        </Modal>
    );
};
